import { useEffect, useState } from "react";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import LoadingButton from "@mui/lab/LoadingButton";
import AppDialog from "../../shared/ui/AppDialog";
import { useNotify } from "../../shared/ui/feedback/SnackbarProvider";
import { reopenErrorOf, reopenPackage } from "./reopen";

export type ReopenDialogProps = {
  open: boolean;
  row: { id: number } | null;
  context?: string;
  onClose: () => void;
  onReopened: () => void;
  /** Abre o pacote vivo do 409 de duplicidade. Sem ele, a mensagem mostra só o número. */
  onOpenPackage?: (id: number) => void;
};

export default function ReopenDialog({
  open,
  row,
  context,
  onClose,
  onReopened,
  onOpenPackage,
}: ReopenDialogProps) {
  const notify = useNotify();
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<{
    message: string;
    livePackageId: number | null;
  } | null>(null);

  // Cada abertura começa limpa.
  useEffect(() => {
    if (open) {
      setText("");
      setError(null);
    }
  }, [open, row?.id]);

  if (!row) return null;

  const confirm = async () => {
    setBusy(true);
    setError(null);
    try {
      await reopenPackage(row.id, text.trim() || undefined);
      notify({
        severity: "success",
        message: "Pacote reaberto: voltou para a empresa.",
      });
      onReopened();
    } catch (e) {
      setError(reopenErrorOf(e));
    } finally {
      setBusy(false);
    }
  };

  const live = error?.livePackageId ?? null;

  return (
    <AppDialog
      open={open}
      onClose={onClose}
      title="Reabrir pacote expirado"
      context={context}
      variant="classic"
      busy={busy}
      actions={
        <>
          <Button onClick={onClose} disabled={busy}>
            Voltar
          </Button>
          <LoadingButton variant="contained" loading={busy} onClick={confirm}>
            Reabrir pacote
          </LoadingButton>
        </>
      }
    >
      <Stack spacing={2}>
        <Typography>
          O pacote volta para “Com a empresa” com o SLA zerado e um novo prazo
          de 10 dias. A reabertura fica no histórico.
        </Typography>
        <TextField
          label="Observação (opcional)"
          value={text}
          onChange={(e) => setText(e.target.value)}
          multiline
          minRows={2}
          inputProps={{ maxLength: 500 }}
          disabled={busy}
        />
        {error && (
          <Alert
            severity="error"
            action={
              live && onOpenPackage ? (
                <Button
                  color="inherit"
                  size="small"
                  sx={{ whiteSpace: "nowrap" }}
                  onClick={() => onOpenPackage(live)}
                >
                  Ver pacote #{live}
                </Button>
              ) : undefined
            }
          >
            {error.message}
          </Alert>
        )}
      </Stack>
    </AppDialog>
  );
}
